import type { FC } from 'react';
import { useMemo } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import { useAtomValue } from 'jotai';

import { apiPaths } from 'src/services/api/apiPaths';
import GuidedTourButton from 'src/components/app/context/GuidedTourContext/components/guidedTourButton';
import { useSection } from 'src/components/app/context/SectionContext';
import ErrorHandler from 'src/components/common/ErrorHandler';
import useOrgTreeState from 'src/components/common/OrgTree/hooks/useOrgTreeState';
import PageFrame from 'src/components/common/PageFrame';
import PageNotFound from 'src/components/common/PageNotFound';
import { dashboardContentLoading } from 'src/components/dashboard/DashboardPage/atom';
import useDashboardChatbot from 'src/components/dashboard/hooks/useDashboardChatbot';

import { useDashboardContext } from '../DashboardContext';
import { useGuidedTourOnDashboard } from '../hooks/useGuidedTourOnDashboard';

import { DashboardContentWrapperMemo } from './components/DashboardContent';
import DashboardSidebar from './components/DashboardSidebar';
import type { DashboardPageProps } from './DashboardPageWrapper';
import { DASHBOARD_PAGE_LOADING_INDICATOR_TEST_ID } from './DashboardPageWrapper';
import { getFlagInOrgTree } from './helper';

import { styles } from './DashboardPage.styles';

export const DashboardPage: FC<DashboardPageProps> = ({ sx = [] }) => {
  const { orgUnits } = useSection();
  const { dashboardConfig, loading } = useDashboardContext();

  const contentLoading = useAtomValue(dashboardContentLoading);
  const orgTreeState = useOrgTreeState();
  const guidedTourOrgTreeId = 'guided-tour-org-tree';
  useGuidedTourOnDashboard(guidedTourOrgTreeId);
  const { chatbotRoomId, chatbotType } = useDashboardChatbot(
    orgTreeState,
    dashboardConfig,
  );

  const firstOrgUnit = useMemo(
    () => (orgUnits || []).slice(0, 1)[0],
    [orgUnits],
  );

  const showFollowUpInspirations = useMemo(
    () =>
      !!dashboardConfig &&
      dashboardConfig.show_follow_up_inspiration_link &&
      getFlagInOrgTree(orgTreeState, 'follow_up_inspirations_link'),
    [dashboardConfig, orgTreeState],
  );

  return (
    <PageFrame
      className="DashboardPage-root"
      sidebar={
        firstOrgUnit ? (
          <DashboardSidebar
            orgTreeState={orgTreeState}
            guidedTourId={guidedTourOrgTreeId}
          />
        ) : undefined
      }
      chatRoomId={chatbotRoomId}
      chatbotType={chatbotType}
      guidedTourButton={<GuidedTourButton loading={contentLoading} />}
      showFollowUpInspirationsButton={
        dashboardConfig?.show_follow_up_inspiration_link &&
        getFlagInOrgTree(orgTreeState, 'follow_up_inspirations_link')
      }
      sx={[styles.dashboardPage, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {loading && (
        <Backdrop
          data-test-id={DASHBOARD_PAGE_LOADING_INDICATOR_TEST_ID}
          invisible
          open={loading}
          sx={styles.loadingIndicator}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      {!loading && (!dashboardConfig || !firstOrgUnit) && (
        <PageNotFound
          headlineKey="pageNotFound.accessDenied"
          explanationKey="pageNotFound.shortExplanation"
        />
      )}

      {!loading && !!dashboardConfig && firstOrgUnit && (
        <DashboardContentWrapperMemo
          dashboardConfig={dashboardConfig}
          showFollowUpInspirations={showFollowUpInspirations}
        />
      )}

      <ErrorHandler
        intercept
        ignoredEndpoints={[
          apiPaths.DASHBOARD.STATISTICS,
          apiPaths.CHATBOT,
          apiPaths.REPORTS.ON_DEMAND,
          apiPaths.DASHBOARD.COMMENTS,
          apiPaths.ORG_UNIT_CHILDREN,
          ...Object.values(apiPaths.VIRTUAL_ASSISTANT),
        ]}
      />
    </PageFrame>
  );
};
